import React from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import CountryProvinceSelector from "../../../components/CountryProvinceSelector";

const splitCommaValues = (value) =>
  String(value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const Step3License = ({
  form,
  handleChange,
  nextStep,
  prevStep,
  countries,
  provinces,
  classes,
  conditions,
}) => {
  const [classInput, setClassInput] = React.useState("");
  const [conditionInput, setConditionInput] = React.useState("");
  React.useEffect(() => {
    setClassInput(
      Array.isArray(form.dl_class) ? form.dl_class.join(",") : form.dl_class || "",
    );
  }, [form.dl_class]);

  React.useEffect(() => {
    setConditionInput(
      Array.isArray(form.dl_condition)
        ? form.dl_condition.join(",")
        : form.dl_condition || "",
    );
  }, [form.dl_condition]);

  const commitSelectInput = (name, inputValue) => {
    const values = [
      ...(Array.isArray(form[name]) ? form[name] : []),
      ...splitCommaValues(inputValue),
    ];

    handleChange({
      target: {
        name,
        value: [...new Set(values)],
      },
    });
  };

  const commitTextInput = (name, rawValue) => {
    handleChange({
      target: {
        name,
        value: splitCommaValues(rawValue),
      },
    });
  };

  const handleMobileDatePointerDown = (event) => {
    if (!window.matchMedia("(max-width: 768px)").matches) return;
    if (document.activeElement === event.currentTarget) return;

    event.preventDefault();
    const input = event.currentTarget;

    input.scrollIntoView({
      block: "end",
      behavior: "smooth",
    });

    setTimeout(() => {
      input.focus();
      if (typeof input.showPicker === "function") {
        input.showPicker();
      } else {
        input.click();
      }
    }, 250);
  };

  // ✅ Keep your handleSubmit
  const handleSubmit = (e) => {
    e.preventDefault();
    commitTextInput("dl_class", classInput);
    commitTextInput("dl_condition", conditionInput);
    if (e.target.checkValidity()) {
      nextStep();
    }
  };

  return (
    <Container className="mt-5">
      <h4 className="mb-4">Driver License Information</h4>
      <Form className="driver-license-form" onSubmit={handleSubmit}>
        <CountryProvinceSelector
          country={form.dl_country}
          province={form.dl_province}
          countries={countries}
          onChange={(field, value) =>
            handleChange({
              target: {
                name: field === "country" ? "dl_country" : "dl_province",
                value,
              },
            })
          }
        />

        <Row className="mb-3">
          {/* License Number - FULL WIDTH ON MOBILE */}
          <Col xs={12} md={3}>
            <Form.Label>
              License Number <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              name="dl_license_no"
              value={form.dl_license_no || ""}
              onChange={handleChange}
              required
            />
          </Col>

          {/* Class - STACKS UNDER LICENSE ON MOBILE */}
          <Col xs={12} md={3} className="mt-3 mt-md-0">
            <Form.Label>
              Class <span className="text-danger">*</span>
            </Form.Label>

            {form.dl_country === "Canada" && classes.length > 0 ? (
              <Select
                isMulti
                inputValue={classInput}
                value={(form.dl_class || []).map((v) => ({
                  value: v,
                  label: v,
                }))}
                options={classes.map((c) => ({
                  value: c.class_code,
                  label: c.description
                    ? `${c.class_code} - ${c.description}`
                    : c.class_code,
                }))}
                onChange={(selected) =>
                  handleChange({
                    target: {
                      name: "dl_class",
                      value: selected.map((s) => s.value),
                    },
                  })
                }
                onInputChange={(value, meta) => {
                  if (meta.action === "input-change") {
                    setClassInput(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "," && classInput.trim()) {
                    e.preventDefault();
                    commitSelectInput("dl_class", classInput);
                    setClassInput("");
                  }
                }}
                onBlur={() => {
                  if (classInput.trim()) {
                    commitSelectInput("dl_class", classInput);
                    setClassInput("");
                  }
                }}
              />
            ) : (
              <Form.Control
                name="dl_class"
                value={classInput}
                onChange={(e) => {
                  setClassInput(e.target.value);
                }}
                onBlur={() => commitTextInput("dl_class", classInput)}
                placeholder="e.g. 1,3,5"
                required
              />
            )}
          </Col>

          {/* Condition */}
          <Col xs={12} md={3} className="mt-3 mt-md-0">
            <Form.Label>Condition</Form.Label>

            {form.dl_country === "Canada" && conditions.length > 0 ? (
              <Select
                isMulti
                inputValue={conditionInput}
                value={(form.dl_condition || []).map((v) => ({
                  value: v,
                  label: v,
                }))}
                options={conditions.map((c) => ({
                  value: c.condition_code,
                  label: c.description
                    ? `${c.condition_code} - ${c.description}`
                    : c.condition_code,
                }))}
                onChange={(selected) =>
                  handleChange({
                    target: {
                      name: "dl_condition",
                      value: selected.map((s) => s.value),
                    },
                  })
                }
                onInputChange={(value, meta) => {
                  if (meta.action === "input-change") {
                    setConditionInput(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "," && conditionInput.trim()) {
                    e.preventDefault();
                    commitSelectInput("dl_condition", conditionInput);
                    setConditionInput("");
                  }
                }}
                onBlur={() => {
                  if (conditionInput.trim()) {
                    commitSelectInput("dl_condition", conditionInput);
                    setConditionInput("");
                  }
                }}
              />
            ) : (
              <Form.Control
                name="dl_condition"
                value={conditionInput}
                onChange={(e) => {
                  setConditionInput(e.target.value);
                }}
                onBlur={() => commitTextInput("dl_condition", conditionInput)}
                placeholder="e.g. A,B,C"
              />
            )}
          </Col>

          {/* Expiry Date */}
          <Col xs={12} md={3} className="mt-3 mt-md-0">
            <Form.Label>
              Expiry Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              name="dl_expdate"
              value={form.dl_expdate || ""}
              onChange={handleChange}
              onPointerDown={handleMobileDatePointerDown}
              required
            />
          </Col>
        </Row>

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={prevStep}
          >
            ← Back
          </button>
          <button type="submit" className="btn btn-primary">
            Next →
          </button>
        </div>
      </Form>
    </Container>
  );
};

export default Step3License;
